import { CartLinesUpdateEvent } from '@shopify/events';

class CartLineVariantEditor extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.updateButton = this.querySelector('[data-variant-update]');
    this.status = this.querySelector('[data-variant-status]');
    this.variants = this.#readJson('[data-variants]');
    this.properties = this.#readJson('[data-properties]');

    this.form?.addEventListener('change', this.#handleChange);
    this.form?.addEventListener('submit', this.#handleSubmit);
    this.#syncSelection();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('change', this.#handleChange);
    this.form?.removeEventListener('submit', this.#handleSubmit);
  }

  #readJson(selector) {
    const element = this.querySelector(selector);
    if (!element) return [];

    try {
      return JSON.parse(element.textContent || '[]');
    } catch {
      return [];
    }
  }

  #handleChange = () => this.#syncSelection();

  #selectedVariant() {
    if (!this.form) return null;

    const selectedOptions = [...this.form.querySelectorAll('fieldset')].map(
      (fieldset) => fieldset.querySelector('input:checked')?.value
    );

    return this.variants.find(
      (variant) =>
        variant.options.length === selectedOptions.length &&
        variant.options.every((option, index) => option === selectedOptions[index])
    );
  }

  #syncSelection() {
    const variant = this.#selectedVariant();
    const isCurrent = String(variant?.id) === this.dataset.variantId;
    const canUpdate = Boolean(variant?.available) && !isCurrent;

    if (this.updateButton) this.updateButton.disabled = !canUpdate;
    if (!this.status) return;

    if (!variant || !variant.available) {
      this.status.textContent = this.dataset.unavailableText || '';
    } else {
      this.status.textContent = '';
    }
  }

  #handleSubmit = async (event) => {
    event.preventDefault();

    const variant = this.#selectedVariant();
    if (!variant?.available || String(variant.id) === this.dataset.variantId) return;

    const quantity = Number(this.dataset.quantity || 1);
    const sellingPlan = Number(this.dataset.sellingPlan || 0);
    const item = {
      id: variant.id,
      quantity,
      properties: this.properties,
    };
    if (sellingPlan) item.selling_plan = sellingPlan;

    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [
          { id: this.dataset.lineKey || '', quantity: 0 },
          { id: String(variant.id), quantity },
        ],
        promise: deferredUpdatePromise.promise,
      })
    );

    this.setAttribute('aria-busy', 'true');
    if (this.updateButton) this.updateButton.disabled = true;
    if (this.status) this.status.textContent = this.dataset.updatingText || '';

    try {
      const addResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [item] }),
      });
      const addResult = await addResponse.json();
      if (!addResponse.ok) throw new Error(addResult.description || addResult.message || 'Unable to add variant');

      const changeResponse = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          id: this.dataset.lineKey,
          quantity: 0,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const updatedCart = await changeResponse.json();
      if (!changeResponse.ok || updatedCart.errors) {
        throw new Error(updatedCart.description || updatedCart.message || updatedCart.errors || 'Unable to update variant');
      }

      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updatedCart),
        detail: {
          sections: updatedCart.sections,
          items: updatedCart.items,
          itemCount: updatedCart.item_count,
          source: 'cart-line-variant-editor',
          didError: false,
        },
      });
    } catch (error) {
      deferredUpdatePromise.reject(error);
      if (this.status) this.status.textContent = error?.message || this.dataset.errorText || '';
      this.#syncSelection();
    } finally {
      this.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('cart-line-variant-editor')) {
  customElements.define('cart-line-variant-editor', CartLineVariantEditor);
}
